<script>
  import { CARDS_PER_PAGE_FACE } from '../../lib/logic/config.js';
  import { IMAGE_TIER_COUNT } from '../../lib/logic/image-load-order.js';
  import CardSlot from './CardSlot.svelte';

  let {
    startIndex = 0,
    endIndex = 0,
    loadTier = IMAGE_TIER_COUNT - 1,
    allCardsData = [],
    currencySymbol = '$',
    onSelect,
    onZoom,
    onTradeQuantity,
  } = $props();

  // Blank page: startIndex and endIndex both -1 (desktop cover)
  const isBlankPage = $derived(startIndex === -1 && endIndex === -1);
  const isInvalidPage = $derived(startIndex < 0);

  // Build the list of cards for this grid
  const gridCards = $derived.by(() => {
    if (isBlankPage || isInvalidPage) return [];

    const cards = [];
    for (let i = startIndex; i < endIndex && i < allCardsData.length; i++) {
      cards.push({ cardInfo: allCardsData[i], originalIndex: i });
    }
    return cards;
  });

  // How many empty slots to pad to fill the 3x3 grid
  const emptySlotCount = $derived.by(() => {
    if (isBlankPage || isInvalidPage) return 0;
    const actualCards = Math.max(
      0,
      Math.min(endIndex - startIndex, allCardsData.length - startIndex)
    );
    return Math.max(0, CARDS_PER_PAGE_FACE - actualCards);
  });
</script>

<div class="card-grid" class:empty-grid={isBlankPage || isInvalidPage}>
  {#if !isBlankPage && !isInvalidPage}
    {#each gridCards as { cardInfo, originalIndex } (originalIndex)}
      <CardSlot
        {cardInfo}
        {originalIndex}
        {loadTier}
        {currencySymbol}
        {onSelect}
        {onZoom}
        {onTradeQuantity}
      />
    {/each}

    {#each Array(emptySlotCount) as _, i (i)}
      <div class="card-slot empty-slot">
        <div class="card-content">
          <div class="card-placeholder"></div>
        </div>
      </div>
    {/each}
  {/if}
</div>

<style>
  .card-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: auto;
    gap: var(--card-grid-gap, 0.75rem);
    align-content: start;
    width: 100%;
    position: relative;
    z-index: 2;
  }

  .card-grid.empty-grid {
    min-height: 100%;
  }

  /* Empty slot styling */
  .empty-slot {
    border-radius: var(--card-corner-radius, 4%);
    overflow: visible;
    aspect-ratio: 5 / 7;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: default;
    z-index: 2;
    background-color: transparent;
    height: auto;
    min-width: 0;
  }

  .empty-slot .card-content {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: var(--card-corner-radius, 4%);
    background-color: transparent;
    overflow: visible;
  }

  .empty-slot .card-placeholder {
    width: 100%;
    height: 100%;
    border-radius: var(--card-corner-radius, 4%);
  }

  /* Stitching pseudo-elements (dashed lines between card slots) */
  .card-grid > :global(.card-slot)::before,
  .card-grid > :global(.card-slot)::after,
  .card-grid > .empty-slot::before,
  .card-grid > .empty-slot::after {
    content: '';
    position: absolute;
    --gap-offset: calc(var(--card-grid-gap, 0.75rem) / 2);
    z-index: var(--z-card-stitch, 4);
    pointer-events: none;
  }

  .card-grid > :global(.card-slot)::before,
  .card-grid > .empty-slot::before {
    top: 0;
    right: calc(-1 * var(--gap-offset));
    width: 1px;
    height: 100%;
    border-right: 1px dashed rgba(255, 255, 255, 0.3);
  }

  .card-grid > :global(.card-slot)::after,
  .card-grid > .empty-slot::after {
    left: 0;
    bottom: calc(-1 * var(--gap-offset));
    width: 100%;
    height: 1px;
    border-bottom: 1px dashed rgba(255, 255, 255, 0.3);
  }

  /* Hide right stitch on 3rd column */
  .card-grid > :global(.card-slot:nth-child(3n))::before,
  .card-grid > .empty-slot:nth-child(3n)::before {
    display: none;
  }

  /* Hide bottom stitch on last row */
  .card-grid > :global(.card-slot:nth-child(n + 7))::after,
  .card-grid > .empty-slot:nth-child(n + 7)::after {
    display: none;
  }
</style>
